"use client";

/**
 * AdminMessageBanner — notification banner for admin-to-user messages.
 *
 * Appears at the top of the viewport when an admin sends an in-app message.
 * Auto-dismisses after 30 seconds. Dismissed message IDs are persisted to
 * localStorage so they don't reappear.
 */

import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { useAdminMessageStore } from "@/stores/adminMessageStore";
import { CloseIcon } from "@/components/icons/CloseIcon";

export function AdminMessageBanner() {
  const activeMessage = useAdminMessageStore((s) => s.activeMessage);
  const dismiss = useAdminMessageStore((s) => s.dismiss);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-dismiss after 30 seconds
  useEffect(() => {
    if (activeMessage) {
      timerRef.current = setTimeout(() => {
        dismiss();
      }, 30_000);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [activeMessage, dismiss]);

  if (!activeMessage) return null;

  return (
    <div
      className={cn(
        "fixed top-0 left-0 right-0 z-[90]",
        "border-b border-border-default",
        "shadow-md",
        "bg-bg-elevated animate-style-admin-message-banner-inline"
      )}
      role="alert"
    >
      <div className="flex items-start gap-3 px-4 py-3 max-w-4xl mx-auto">
        {/* Orange left accent bar */}
        <div className="flex-shrink-0 w-1 self-stretch rounded-full bg-palette-orange" />

        {/* Message content */}
        <div className="flex-1 min-w-0">
          <p className="text-3 font-medium text-fg-subtle uppercase tracking-wide">
            Message from Bash Team
          </p>
          {activeMessage.subject && (
            <p className="text-3.5 font-semibold text-fg-default mt-0.5">
              {activeMessage.subject}
            </p>
          )}
          <p className="text-3.5 text-fg-muted mt-0.5 line-clamp-3">
            {activeMessage.content}
          </p>
        </div>

        {/* Dismiss button */}
        <button
          onClick={dismiss}
          className="flex-shrink-0 p-1 rounded-12 text-fg-subtle hover:text-fg-default transition-colors"
          aria-label="Dismiss notification"
        >
          <CloseIcon width={14} height={14} />
        </button>
      </div>
    </div>
  );
}
